/**
 * Statistici Body Parts - Refactorizat conform arhitecturii RIS
 * Reflect noua logică: Vocabular → Segmente → Mapping Rules → Detecție
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Category as SegmentsIcon,
  Rule as MappingIcon,
  CheckCircle as ApprovedIcon,
  Assessment as StudiesIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
  Inventory as VocabularyIcon,
  Speed as PerformanceIcon,
} from '@mui/icons-material';
import { bodyPartsApi, BodyPartsStats as BodyPartsStatsType } from '../../services/bodyPartsApi';

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  progress?: number;
  trend?: {
    value: string;
    positive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  progress,
  trend,
}) => (
  <Card elevation={1} sx={{ height: '100%' }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" flex={1}>
        <Box flex={1}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }} color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }} color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.72rem' }} color="textSecondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Chip
              size="small"
              icon={<TrendingIcon sx={{ fontSize: 12 }} />}
              label={trend.value}
              color={trend.positive ? 'success' : 'error'}
              sx={{ mt: 0.5, height: 18, fontSize: '0.68rem' }}
            />
          )}
          {progress !== undefined ? (
            <Box mt={1}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                color={color}
                sx={{ height: 5, borderRadius: 2 }}
              />
              <Typography sx={{ fontSize: '0.68rem', mt: 0.25 }} color="textSecondary">
                {progress.toFixed(1)}%
              </Typography>
            </Box>
          ) : (
            <Box mt={1} sx={{ height: '20px' }} />
          )}
        </Box>
        <Box color={`${color}.main`} sx={{ ml: 1 }}>
          {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 28 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface BodyPartsStatsProps {
  onNeedRefresh?: () => void;
}

export const BodyPartsStats: React.FC<BodyPartsStatsProps> = ({
  onNeedRefresh: _onNeedRefresh
}) => {
  const [stats, setStats] = useState<BodyPartsStatsType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bodyPartsApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading body parts stats:', error);
      setError('Eroare la încărcarea statisticilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton size="small" onClick={loadStats}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {error || 'Nu s-au putut încărca statisticile'}
      </Alert>
    );
  }

  // Calculăm KPI-uri
  const mappingSuccessRate = stats.total_mappings > 0 
    ? (stats.approved_mappings / stats.total_mappings) * 100 
    : 0;
    
  const studiesProcessedRate = (stats.studies_with_body_parts + stats.studies_without_body_parts) > 0
    ? (stats.studies_with_body_parts / (stats.studies_with_body_parts + stats.studies_without_body_parts)) * 100
    : 0;

  const totalStudies = stats.studies_with_body_parts + stats.studies_without_body_parts;
  
  const activationRate = stats.total_nomenclator_items > 0
    ? (stats.active_nomenclator_items / stats.total_nomenclator_items) * 100
    : 0;

  // Pregătim datele pentru distribuția pe modalități
  const modalityData = Object.entries(stats.categories || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10); // Top 10 modalități

  return (
    <Box p={1.5}>
      {/* Header cu refresh */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Dashboard Statistici
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }} color="textSecondary">
            Monitorizare sistem detecție body parts
          </Typography>
        </Box>
        <Tooltip title="Reîmprospătează datele">
          <IconButton onClick={loadStats} disabled={loading} color="primary" size="small">
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Alertă pentru mapping-uri pending */}
      {stats.pending_mappings > 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 1.5, 
            py: 0.5,
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            '& .MuiAlert-icon': { color: 'warning.contrastText' }
          }}
        >
          <Typography sx={{ fontSize: '0.78rem', color: 'inherit' }}>
            <strong>{stats.pending_mappings} mapping-uri</strong> necesită review manual.
          </Typography>
        </Alert>
      )}

      {/* SECȚIUNEA 1: Overview General */}
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
        📊 Imagine de Ansamblu
      </Typography>
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Segmente Definite"
            value={stats.active_nomenclator_items}
            subtitle={`${stats.total_nomenclator_items} total`}
            icon={<SegmentsIcon />}
            color="primary"
            progress={activationRate}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Mapping Rules"
            value={stats.total_mappings}
            subtitle={`${stats.approved_mappings} aprobate`}
            icon={<MappingIcon />}
            color="secondary"
            progress={mappingSuccessRate}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Studii Procesate"
            value={`${studiesProcessedRate.toFixed(1)}%`}
            subtitle={`${stats.studies_with_body_parts}/${totalStudies}`}
            icon={<StudiesIcon />}
            color="success"
            progress={studiesProcessedRate}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Activitate 24h"
            value={stats.recent_extractions}
            subtitle="extrageri"
            icon={<PerformanceIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* SECȚIUNEA 2: Calitate & Performance */}
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
        ⚡ Calitate & Performance
      </Typography>
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} md={4}>
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={1}>
                <ApprovedIcon color="success" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Status Mapping-uri</Typography>
              </Box>
              
              <Box mb={1}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: '0.75rem' }}>Aprobate</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} color="success.main">
                    {stats.approved_mappings}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={mappingSuccessRate} 
                  color="success"
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>

              <Box mb={1}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: '0.75rem' }}>Pending</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} color="warning.main">
                    {stats.pending_mappings}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.total_mappings > 0 ? (stats.pending_mappings / stats.total_mappings) * 100 : 0}
                  color="warning"
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>

              <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: '0.75rem' }}>Rejected</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} color="error.main">
                    {stats.total_mappings - stats.approved_mappings - stats.pending_mappings}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.total_mappings > 0 
                    ? ((stats.total_mappings - stats.approved_mappings - stats.pending_mappings) / stats.total_mappings) * 100 
                    : 0}
                  color="error"
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={1}>
                <VocabularyIcon color="primary" sx={{ fontSize: 18, mr: 0.5 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Distribuție pe Modalități</Typography>
              </Box>
              
              {modalityData.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        bgcolor: 'grey.900',
                        '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 }
                      }}>
                        <TableCell>Modalitate</TableCell>
                        <TableCell align="right">Segmente</TableCell>
                        <TableCell>Distribuție</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalityData.map(([modality, count]) => {
                        const percentage = stats.active_nomenclator_items > 0 
                          ? (count / stats.active_nomenclator_items) * 100 
                          : 0;
                        return (
                          <TableRow key={modality} sx={{ '& td': { py: 0.5, px: 1, fontSize: '0.78rem' } }}>
                            <TableCell>
                              <Chip label={modality} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{count}</Typography>
                            </TableCell>
                            <TableCell>
                              <LinearProgress variant="determinate" value={percentage} sx={{ height: 5, borderRadius: 2 }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.75rem' }} color="textSecondary">{percentage.toFixed(1)}%</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: '0.78rem' }} color="textSecondary" textAlign="center" py={2}>
                  Nu există date
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* SECȚIUNEA 3: Studii & Detecție */}
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
        🔍 Studii & Detecție
      </Typography>
      <Card elevation={1}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center" p={1}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }} color="primary.main">
                  {totalStudies.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem' }} color="textSecondary">
                  Total Studii
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-around">
                  <Box>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }} color="success.main">
                      {stats.studies_with_body_parts.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem' }} color="textSecondary">Cu Detecție</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }} color="warning.main">
                      {stats.studies_without_body_parts.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem' }} color="textSecondary">Fără</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Box p={1}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 1 }}>
                  Progres Detecție
                </Typography>
                
                <Box mb={1.5}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography sx={{ fontSize: '0.75rem' }}>Body parts detectate</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }} color="success.main">
                      {studiesProcessedRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={studiesProcessedRate} 
                    color="success"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {stats.recent_extractions > 0 && (
                  <Box p={1} bgcolor="info.dark" borderRadius={1}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'info.contrastText' }}>
                      🔄 <strong>{stats.recent_extractions}</strong> extrageri în ultimele 24h
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
